/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: index.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  06.07.22 5:01 pm.
 * Last modified: 06.07.22 5:01 pm.
 *
 * More information at: https://egt-digital.com/
 */

import React from 'react';
import { addBuildHash, getBannerOrientation, isOnlyMaxBetGame, numberWithThousandSeparator } from '../api/helpers';
import {
    GAMES_WITH_CUSTOM_BADGES,
    gamesWithAnimatedBanners,
    HORIZONTAL_JP_BADGES,
    VERTICAL_JP_BADGES,
    X_RIDE_MP,
} from '../constants';
import { useLobbyHost, useSettings } from '../contexts/SettingsContext';
import store from '../eventStore';
import AnimatedBanner from './AnimatedBanner';
import ButtonFavorite from './ButtonFavorite';
import ButtonPlay from './ButtonPlay';
// un comment CurrentPlayersCount when number to display is approved
// import CurrentPlayersCount from './CurrentPlayersCount';
import JackpotBottomBand from './JackpotBottomBand';
import { IJackpotLabelProps } from './JackpotLabel';
import MostPlayedNums from './MostPlayedNums';
import TagsContainer from './TagsContainer';
import { useEffectWithRef } from '../hooks/useEffectWithRef';
import CurrentPlayersCount from './CurrentPlayersCount';
import { useTranslator } from '../contexts/TranslatorContext';

const Game = ({
    cols,
    rows,
    name,
    gameKey,
    jackpotName,
    jackpotCurrency,
    jackpotComponent,
    jackpotComponentRenderInside,
    minDenomination,
    branding,
    mostPlayedIndex,
    children,
    onClickPlay,
    minBet,
    maxBet,
}: IGameProps) => {
    const [settings] = useSettings();
    const host = useLobbyHost();
    const translate = useTranslator();

    const showOnlyMaxBet = isOnlyMaxBetGame(gameKey);

    const [setGameImageRef] = useEffectWithRef(node => {
        if (node) {
            node.onload = () => {
                node?.parentElement?.classList.add('egt_banner_fade_in_image');
                // ugly fix for safari browsers to force redraws
                setTimeout(() => {
                    const parentEl = node?.parentElement;
                    if (parentEl) {
                        parentEl.style.display = 'none';
                        // request the no unused var just to force redraw
                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                        parentEl.offsetHeight;
                        parentEl.style.display = '';
                    }
                }, 2);
            };
        }
    });

    const handleIsHovered = (e: React.MouseEvent<HTMLElement>): void => {
        if (settings.mode === 'desktop') {
            store.HOVER_GAME.next(gameKey);
        }
    };

    const handleClickMobile = (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
        if (settings.mode !== 'desktop' && !(e.target as HTMLElement).classList.contains('egt_game_btn_favorite')) {
            onClickPlay?.();
        }
    };

    const hasJackpotComponentFor = (renderFor: 'tags' | 'block') => {
        if (renderFor === jackpotComponentRenderInside) {
            if (gameKey === X_RIDE_MP) {
                // TODO: refactor this using other approach for rendering such non jackpot labels
                return true;
            }
            return jackpotName && minDenomination;
        } else {
            return false;
        }
    };

    const getJackpotComponentFor = (renderFor: 'tags' | 'block') => {
        if (renderFor === jackpotComponentRenderInside) {
            const JackpotComponent = jackpotComponent;
            if (gameKey === X_RIDE_MP) {
                // TODO: refactor this using other approach for rendering such non jackpot labels
                return <JackpotComponent currency={''} jackpotName={''} denomination={0} />;
            }
            return (
                jackpotName &&
                minDenomination && (
                    <JackpotComponent
                        jackpotName={jackpotName}
                        currency={jackpotCurrency || 'EGT'}
                        denomination={minDenomination}
                    />
                )
            );
        } else {
            return <></>;
        }
    };

    const orientation = getBannerOrientation({ rows, cols });
    const mainBannerUrl = `${host}/assets/portal${branding ? '/brandings/' + branding.toLowerCase() : ''}`;
    const hasCustomJpBadge = Object.keys(GAMES_WITH_CUSTOM_BADGES).includes(gameKey);

    // TODO:: DELETE THIS BEFORE MERGE INTO PRE-RELEASE
    if (jackpotName === 'InstantLink') {
        minBet = 0.2;
        maxBet = 0.8;
    }

    return (
        <div
            className={`egt_game egtd_lobby_${orientation}`}
            style={{
                gridRowStart: `span ${rows}`,
                gridColumnStart: `span ${cols}`,
            }}
            onMouseEnter={handleIsHovered}
            onClick={handleClickMobile}
        >
            <img
                src={addBuildHash(`${mainBannerUrl}/verticals/${gameKey}.png`)}
                alt={name}
                loading="lazy"
                ref={setGameImageRef}
            />
            {gamesWithAnimatedBanners.includes(gameKey) && (
                <AnimatedBanner src={addBuildHash(`${mainBannerUrl}/animatedBanners/verticals/${gameKey}.mp4`)} />
            )}
            {jackpotName &&
                [...VERTICAL_JP_BADGES, ...HORIZONTAL_JP_BADGES].indexOf(
                    jackpotName
                        .split(' ')
                        .join('_')
                        .toLowerCase(),
                ) !== -1 && (
                    <img
                        src={addBuildHash(
                            `${host}/assets/portal/badges/${jackpotName
                                .split(' ')
                                .join('_')
                                .toLowerCase()}_plate.png`,
                        )}
                        className={`badge_plate badge_plate_${jackpotName
                            .toLowerCase()
                            .split(' ')
                            .join('_')}`}
                        alt={jackpotName}
                        loading="lazy"
                        decoding="async"
                    />
                )}
            <TagsContainer>
                {jackpotName && (
                    <img
                        src={addBuildHash(
                            `${host}/assets/portal/badges/${
                                hasCustomJpBadge
                                    ? GAMES_WITH_CUSTOM_BADGES[gameKey]
                                    : jackpotName
                                          .split(' ')
                                          .join('_')
                                          .toLowerCase()
                            }.png`,
                        )}
                        className={`badge badge_${jackpotName
                            .toLowerCase()
                            .split(' ')
                            .join('_')}`}
                        alt={jackpotName}
                        loading="lazy"
                        decoding="async"
                    />
                )}
            </TagsContainer>
            {getJackpotComponentFor('block')}
            {jackpotComponentRenderInside === 'tags' && (
                <div className="egt_game_bottom_container">
                    {!!minBet && !!maxBet && (
                        <div
                            className={`name ${
                                !hasJackpotComponentFor(jackpotComponentRenderInside) ? 'without-jp' : ''
                            }`}
                        >
                            <div className="min-max-bet">
                                {!showOnlyMaxBet && (
                                    <>
                                        <span>{`${numberWithThousandSeparator(minBet)} - ${
                                            maxBet < 10
                                                ? numberWithThousandSeparator(maxBet)
                                                : numberWithThousandSeparator(maxBet, 0)
                                        }`}</span>
                                        <span className="currency">{`${settings.currency}`}</span>
                                    </>
                                )}
                                {showOnlyMaxBet && (
                                    <>
                                        <span>
                                            {maxBet < 10
                                                ? numberWithThousandSeparator(maxBet)
                                                : numberWithThousandSeparator(maxBet, 0)}
                                        </span>
                                        <span className="max-bet-text">&nbsp;&nbsp;{translate('MAX_BET')}</span>
                                        <span className="currency">{`${settings.currency}`}</span>
                                    </>
                                )}
                            </div>
                            <CurrentPlayersCount gameKey={gameKey} />
                        </div>
                    )}
                    {getJackpotComponentFor('tags')}
                </div>
            )}
            {children}
            <ButtonPlay onClick={onClickPlay} />
            <ButtonFavorite gameKey={gameKey} />
            {Boolean(mostPlayedIndex) && mostPlayedIndex && <MostPlayedNums index={mostPlayedIndex} />}
        </div>
    );
};

export interface IGameProps {
    gameKey: string;
    jackpotName?: string;
    jackpotCurrency?: string;
    name: string;
    cols: number;
    rows: number;
    minDenomination?: number;
    jackpotComponent: React.ComponentType<IJackpotLabelProps>;
    jackpotComponentRenderInside: 'tags' | 'block';
    branding: string | null;
    onClickPlay?: () => void;
    children?: React.ReactNode;
    mostPlayedIndex?: string;
    isViewingAll?: boolean;
    minBet: number;
    maxBet: number;
}

export const gameDefaultProps = {
    cols: 1,
    rows: 1,
    jackpotComponent: JackpotBottomBand,
    jackpotComponentRenderInside: 'tags',
    branding: null,
};

Game.defaultProps = gameDefaultProps;

export default Game;
